// ============================================
// API Service
// ============================================

import { User, Child, Persona, ChatResponse, AdminStats } from '../types';

// API 基础 URL - 生产环境使用完整后端地址
// 直接硬编码，确保生产环境正确指向后端
const API_BASE = 'https://equantu-kids-api.doziva228.workers.dev/api';

// ============================================
// Helper Functions
// ============================================

function getAuthState() {
  try {
    const stored = localStorage.getItem('equantu-auth');
    if (stored) {
      return JSON.parse(stored).state;
    }
  } catch {
    return null;
  }
  return null;
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const state = getAuthState();
  const token = state?.token;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

async function fetchWithAdminAuth(url: string, options: RequestInit = {}) {
  const state = getAuthState();
  const token = state?.adminToken;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// ============================================
// Auth API
// ============================================

export const authAPI = {
  async register(email: string, password: string, name?: string) {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Registration failed' }));
      throw new Error(error.error || 'Registration failed');
    }

    return response.json();
  },

  async login(email: string, password: string) {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(error.error || 'Login failed');
    }

    return response.json();
  },

  async adminLogin(email: string, password: string) {
    const response = await fetch(`${API_BASE}/auth/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Admin login failed' }));
      throw new Error(error.error || 'Admin login failed');
    }

    return response.json();
  },

  async getMe(): Promise<{ user: User }> {
    return fetchWithAuth('/auth/me');
  },

  async logout() {
    return fetchWithAuth('/auth/logout', { method: 'POST' });
  },
};

// ============================================
// Children API
// ============================================

export const childrenAPI = {
  async list(): Promise<{ children: Child[] }> {
    return fetchWithAuth('/children');
  },

  async getAll(): Promise<{ children: Child[] }> {
    return fetchWithAuth('/children');
  },

  async get(id: string): Promise<{ child: Child }> {
    return fetchWithAuth(`/children/${id}`);
  },

  async create(data: {
    name: string;
    nickname?: string;
    birthDate?: string;
    age_group?: '3-5' | '6-8' | '9-12';
    ageGroup?: '3-5' | '6-8' | '9-12';
    language?: string;
  }): Promise<{ child: Child }> {
    // Normalize the data
    const normalizedData = {
      name: data.name,
      nickname: data.nickname,
      birthDate: data.birthDate,
      ageGroup: data.age_group || data.ageGroup || '6-8',
      language: data.language || 'zh',
    };
    return fetchWithAuth('/children', {
      method: 'POST',
      body: JSON.stringify(normalizedData),
    });
  },

  async update(id: string, data: Partial<Child>): Promise<{ message: string }> {
    return fetchWithAuth(`/children/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async delete(id: string): Promise<{ message: string }> {
    return fetchWithAuth(`/children/${id}`, { method: 'DELETE' });
  },

  async getHistory(id: string, limit = 20, offset = 0) {
    return fetchWithAuth(`/children/${id}/history?limit=${limit}&offset=${offset}`);
  },
};

// ============================================
// Personas API
// ============================================

export const personasAPI = {
  async getAll(): Promise<{ personas: Persona[] }> {
    return fetchWithAuth('/personas');
  },

  async get(id: string): Promise<{ persona: Persona }> {
    return fetchWithAuth(`/personas/${id}`);
  },
};

// ============================================
// Chat API
// ============================================

export const chatAPI = {
  async sendMessage(
    childId: string,
    personaId: string,
    message: string,
    sessionId?: string
  ): Promise<ChatResponse> {
    return fetchWithAuth('/chat/message', {
      method: 'POST',
      body: JSON.stringify({ childId, personaId, message, sessionId }),
    });
  },

  async getHistory(childId: string, limit = 20, offset = 0) {
    return fetchWithAuth(`/chat/history/${childId}?limit=${limit}&offset=${offset}`);
  },

  async getSession(sessionId: string) {
    return fetchWithAuth(`/chat/session/${sessionId}`);
  },

  async getMessages(sessionId: string) {
    return fetchWithAuth(`/chat/session/${sessionId}/messages`);
  },

  async endSession(sessionId: string) {
    return fetchWithAuth(`/chat/session/${sessionId}/end`, { method: 'POST' });
  },

  async deleteSession(sessionId: string) {
    return fetchWithAuth(`/chat/session/${sessionId}`, { method: 'DELETE' });
  },
};

// ============================================
// Voice API
// ============================================

export const voiceAPI = {
  async transcribe(audioBlob: Blob, language = 'zh'): Promise<{ text: string }> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');
    formData.append('language', language);

    const state = getAuthState();
    const token = state?.token;

    const response = await fetch(`${API_BASE}/voice/asr`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Speech recognition failed');
    }

    return response.json();
  },

  async synthesize(text: string, personaId: string): Promise<{ audioId: string; audioUrl: string }> {
    return fetchWithAuth('/voice/tts', {
      method: 'POST',
      body: JSON.stringify({ text, personaId }),
    });
  },

  getAudioUrl(audioId: string): string {
    return `${API_BASE}/voice/audio/${audioId}`;
  },
};

// ============================================
// Admin API
// ============================================

export const adminAPI = {
  async getStats(): Promise<{ stats: AdminStats }> {
    return fetchWithAdminAuth('/admin/stats');
  },

  async getConversations(params?: {
    limit?: number;
    offset?: number;
    childId?: string;
    personaId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.childId) queryParams.set('childId', params.childId);
    if (params?.personaId) queryParams.set('personaId', params.personaId);
    if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.set('dateTo', params.dateTo);

    return fetchWithAdminAuth(`/admin/conversations?${queryParams.toString()}`);
  },

  async getConversationMessages(sessionId: string) {
    return fetchWithAdminAuth(`/admin/conversations/${sessionId}/messages`);
  },

  async getTokenStats(days = 30, provider?: string) {
    const params = new URLSearchParams({ days: days.toString() });
    if (provider) params.set('provider', provider);
    return fetchWithAdminAuth(`/admin/tokens?${params.toString()}`);
  },

  async getUsers(limit = 50, offset = 0) {
    return fetchWithAdminAuth(`/admin/users?limit=${limit}&offset=${offset}`);
  },

  async getConfig() {
    return fetchWithAdminAuth('/admin/config');
  },

  async updateConfig(key: string, value: any) {
    return fetchWithAdminAuth(`/admin/config/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  async exportConversations(params?: { dateFrom?: string; dateTo?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.set('dateTo', params.dateTo);

    return fetchWithAdminAuth(`/admin/export/conversations?${queryParams.toString()}`);
  },
};
